import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Coins, Info } from "lucide-react";

// Iter 77 — Read-only per-vertical rebate preview. All numbers come from
// `/api/players/me/rebate-preview` which reuses the SAME tier math as the
// marketplace card (compute_unlock_pricing_sync). No client-side pricing.
// Display only — no money movement.
const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
const pct = (x) => `${Math.round((x ?? 0) * 100)}%`;

function VerticalRow({ vertical, data, currentRate, maxRate, t }) {
  const label = vertical === "casino" ? t("dash.rebatePreview.casino") : t("dash.rebatePreview.case");
  if (!data?.hasOffer) {
    return (
      <div
        className="border border-white/10 p-4"
        data-testid={`rp-${vertical}-empty`}
      >
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-2">{label}</div>
        <div className="text-xs text-[#8B97AE]" data-testid={`rp-${vertical}-empty-copy`}>
          {t("dash.rebatePreview.empty")}
        </div>
      </div>
    );
  }
  // COMPLIANCE — never render the VG-commission intermediate figure to the
  // player. The rebate is computed server-side; the client only receives and
  // shows: offered volume, the player's current rate, and the estimated $
  // rebate (+ the max-potential incentive).
  return (
    <div
      className="border border-white/10 p-4 space-y-3"
      data-testid={`rp-${vertical}`}
    >
      <div className="flex items-baseline justify-between">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">{label}</div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#2DD4A7]" data-testid={`rp-${vertical}-rate`}>
          {t("dash.rebatePreview.yourRate")}: {pct(currentRate)}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-1">{t("dash.rebatePreview.offeredVolume")}</div>
          <div className="font-mono text-lg text-[#E8ECF4] tabular-nums" data-testid={`rp-${vertical}-offered`}>
            ${fmt(data.offeredVolume)}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-1">{t("dash.rebatePreview.expected")}</div>
          <div className="font-mono text-2xl text-[#2DD4A7] tabular-nums" data-testid={`rp-${vertical}-current`}>
            ${fmt(data.currentRebate)}
          </div>
        </div>
      </div>
      <div className="pt-2 border-t border-white/5 flex items-baseline justify-between">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">
          {t("dash.rebatePreview.maxLabel").replace("{max}", pct(maxRate))}
        </div>
        <div className="font-mono text-sm text-[#F5B841] tabular-nums" data-testid={`rp-${vertical}-max`}>
          ${fmt(data.maxRebate)}
        </div>
      </div>
    </div>
  );
}

export default function RebatePreview() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/players/me/rebate-preview")
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setErr(e?.response?.data?.detail || "Failed to load cashback preview"); });
    return () => { alive = false; };
  }, []);

  if (err) {
    return (
      <section className="lg:col-span-12 border border-white/15 p-6" data-testid="rebate-preview-error">
        <div className="text-xs text-[#F26D6D] font-mono">{String(err)}</div>
      </section>
    );
  }
  if (!data) {
    return (
      <section className="lg:col-span-12 border border-white/15 p-6" data-testid="rebate-preview-loading">
        <div className="text-xs text-[#8B97AE] font-mono">…</div>
      </section>
    );
  }
  const currentRate = data.currentRate ?? 0.10;
  const maxRate = data.maxRate ?? 0.20;
  return (
    <section
      className="lg:col-span-12 border border-white/15 p-6 lg:p-8"
      data-testid="rebate-preview"
    >
      <div className="flex items-center gap-2 mb-1">
        <Coins className="w-4 h-4 text-[#2DD4A7]" />
        <h2 className="font-display font-bold text-lg" data-testid="rebate-preview-title">
          {t("dash.rebatePreview.title")}
        </h2>
      </div>
      <div className="flex items-start gap-2 mb-5">
        <Info className="w-3.5 h-3.5 text-[#F5B841] mt-0.5 shrink-0" />
        <p className="text-xs text-[#F5B841]" data-testid="rebate-preview-estimate-note">
          {t("dash.rebatePreview.estimateNote")}
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <VerticalRow
          vertical="casino"
          data={data.verticals?.casino}
          currentRate={currentRate}
          maxRate={maxRate}
          t={t}
        />
        <VerticalRow
          vertical="case_opening"
          data={data.verticals?.case_opening}
          currentRate={currentRate}
          maxRate={maxRate}
          t={t}
        />
      </div>
      <p
        className="mt-4 text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]"
        data-testid="rebate-preview-footnote"
      >
        {t("dash.rebatePreview.footnote")}
      </p>
    </section>
  );
}
